import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import useWidgetData from '../hooks/useWidgetData';
import { transformWidgetData } from '../widgetConfigSchema';
import { useFilterStoreWithMomentDates } from '../../../store/filterStore.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import WidgetEmptyState from '../WidgetEmptyState';

/**
 * ResourceChangesOverview Widget - Shows resource changes aggregated per resource cluster
 * Displays icons horizontally with change numbers (green for positive, red for negative)
 * Only shows clusters with non-zero changes
 */
const ResourceChangesOverviewWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false
}) => {
  const { startDate, endDate } = useFilterStoreWithMomentDates();

  // Stable filters to avoid re-fetch loops when filters is undefined or recreated
  const stableFilters = useMemo(() => filters || [], [filters]);

  // Build a non-mutating config with updated dates merged into the request body; memoize to keep reference stable
  const finalConfig = useMemo(() => {
    const dataSource = config?.dataSource || {};
    const baseBody = dataSource.body || {};
    
    // Only add dates if both are present to avoid calling .format on null/undefined
    const bodyWithDates = (startDate && endDate) ? {
      ...baseBody,
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    } : baseBody;

    return {
      ...config,
      dataSource: {
        ...dataSource,
        method: dataSource.method || 'POST',
        body: bodyWithDates
      }
    };
  }, [config, startDate, endDate]);

  const { data, isLoading, error } = useWidgetData(finalConfig, stableFilters);

  // Transform data if it comes from API
  const widgetData = data ? transformWidgetData('resourcechangesoverview', data) : null;

  // Aggregate changes by resource cluster
  const hideZeroNetChangeClusters = config?.hideZeroNetChangeClusters !== false;

  const clusterChanges = useMemo(() => {
    if (!widgetData || !widgetData.data || !Array.isArray(widgetData.data)) {
      return [];
    }

    // Get selected clusters and order from config
    const selectedClusters = config?.selectedClusters || [];
    const clusterOrder = config?.clusterOrder || [];

    // Aggregate net change (plus_change - min_change) by grouping (resource_cluster)
    const aggregated = {};
    
    widgetData.data.forEach(item => {
      // API returns 'grouping' field (mapped from group_name in SQL)
      const cluster = item.grouping || item.group_name || item.resource_cluster;
      if (!cluster) return;
      
      // Calculate net change: plus_change - min_change
      const plusChange = item.plus_change || 0;
      const minChange = item.min_change || 0;
      const netChange = Math.abs(plusChange) - Math.abs(minChange);

      if (!aggregated[cluster]) {
        aggregated[cluster] = {
          cluster,
          totalChange: 0
        };
      }
      
      aggregated[cluster].totalChange += netChange;
    });

    // Convert to array and filter
    let filtered = Object.values(aggregated);
    
    // Filter by selected clusters if specified, otherwise show all
    if (selectedClusters.length > 0) {
      filtered = filtered.filter(item => selectedClusters.includes(item.cluster));
    }
    
    // Filter out zero changes if configured
    if (hideZeroNetChangeClusters) {
      filtered = filtered.filter(item => item.totalChange !== 0);
    }

    // Sort by configured order if specified, otherwise use default order
    if (clusterOrder.length > 0) {
      const orderMap = new Map(clusterOrder.map((cluster, index) => [cluster, index]));
      return filtered.sort((a, b) => {
        const aIndex = orderMap.get(a.cluster);
        const bIndex = orderMap.get(b.cluster);
        if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
        if (aIndex !== undefined) return -1;
        if (bIndex !== undefined) return 1;
        // If not in order, use default order
        const defaultOrder = ['Compute', 'Database', 'Storage', 'Networking', 'Other'];
        const aDefaultIndex = defaultOrder.indexOf(a.cluster);
        const bDefaultIndex = defaultOrder.indexOf(b.cluster);
        if (aDefaultIndex !== -1 && bDefaultIndex !== -1) return aDefaultIndex - bDefaultIndex;
        return a.cluster.localeCompare(b.cluster);
      });
    }

    // Default sorting if no order specified
    const defaultOrder = ['Compute', 'Database', 'Storage', 'Networking', 'Other'];
    return filtered.sort((a, b) => {
      const aIndex = defaultOrder.indexOf(a.cluster);
      const bIndex = defaultOrder.indexOf(b.cluster);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.cluster.localeCompare(b.cluster);
    });
  }, [widgetData, hideZeroNetChangeClusters, config?.selectedClusters, config?.clusterOrder]);

  // Icon mapping for resource clusters
  const getClusterIcon = (cluster) => {
    switch (cluster) {
      case 'Compute':
        return icon({ name: 'computer', family: 'classic', style: 'regular' });
      case 'Database':
        return icon({ name: 'database', family: 'classic', style: 'regular' });
      case 'Storage':
        return icon({ name: 'box-archive', family: 'classic', style: 'regular' });
      case 'Networking':
        return icon({ name: 'wifi', family: 'classic', style: 'regular' });
      case 'Other':
        return icon({ name: 'circle', family: 'classic', style: 'regular' });
      default:
        return icon({ name: 'circle', family: 'classic', style: 'regular' });
    }
  };

  // Hide title when there's no data
  const hasData = clusterChanges.length > 0;

  const emptyStateTitle = !widgetData ? 'No data available' : 'No resource changes';
  const emptyStateDescription = !widgetData
    ? 'Try adjusting your filters or date range.'
    : 'No resource changes found for the selected period.';
  const emptyStateIcon = icon({ name: 'chart-line', family: 'classic', style: 'regular' });

  return (
    <BaseWidget
      widgetId={widgetId}
      title={hasData ? config.title : undefined}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      <div 
        className="h-full flex flex-col items-center justify-center p-4"
        style={{ minHeight: '100px' }}
      >
        {clusterChanges.length > 0 ? (
          <div className="flex flex-row items-center justify-center gap-20 flex-wrap">
            {clusterChanges.map((item) => {
              const isPositive = item.totalChange > 0;
              const changeColor = isPositive ? 'text-success' : 'text-danger';
              
              return (
                <div
                  key={item.cluster}
                  className="flex flex-col items-center gap-2 cursor-default"
                >
                  <FontAwesomeIcon
                    icon={getClusterIcon(item.cluster)}
                    className="text-text-2" style={{ fontSize: "32px" }}
                  />
                  <span className={`text-lg font-semibold ${changeColor}`}>
                    {item.totalChange > 0 ? '+' : ''}{item.totalChange}
                  </span>
                  <span className="text-sm text-text-2">
                    {item.cluster}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <WidgetEmptyState
            title={emptyStateTitle}
            description={emptyStateDescription}
            icon={emptyStateIcon}
          />
        )}
      </div>
    </BaseWidget>
  );
};

ResourceChangesOverviewWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    hideZeroNetChangeClusters: PropTypes.bool,
    selectedClusters: PropTypes.arrayOf(PropTypes.string),
    clusterOrder: PropTypes.arrayOf(PropTypes.string),
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool
};

export default ResourceChangesOverviewWidget;

